import { Trans } from "@lingui/macro";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import { UiBox, UiSpinner } from "@smp/ui";
import { FC, useCallback, useMemo } from "react";
import { useUpdateEffect } from "react-use";

import { useContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import {
  useQueryPolotnoTemplates,
  useQueryPolotnoUnsplash,
} from "@/queries/polotno";
import { yaReachGoal } from "@/utils/ya_reach_goal";

import { NothingFound, VirtualGrid } from "./components";

export const Content: FC<{
  onChangeRelevantFlag?: (hasTemplates: boolean) => void;
}> = ({ onChangeRelevantFlag }) => {
  const {
    state: { selectedTags, searchString, sortBy },
  } = useContextPolotnoTemplates();

  const {
    data: polotnoTemplatesData,
    isLoading: isTemplatesLoading,
    isFetching: isTemplatesFetching,
    hasNextPage: hasPolotnoTemplatesNextPage,
    fetchNextPage: polotnoTemplatesFetchNextPage,
  } = useQueryPolotnoTemplates({
    tags__slug__all: selectedTags,
    search: searchString || undefined,
    ordering: sortBy,
    page_size: 100,
  });

  const { data: unsplashResp, isFetching: isUnsplashLoading } =
    useQueryPolotnoUnsplash(searchString, {
      enabled: !!searchString,
    });

  const polotnoTemplates = useMemo(
    () =>
      polotnoTemplatesData?.pages
        .flatMap(({ results }) => results)
        .map((template) => {
          const templateSize = template.tags
            .find((tag) => tag.startsWith("size_"))
            ?.replace(/^size_/, "")
            ?.split("x");

          const width = !Number.isNaN(Number(templateSize?.[0]))
            ? Number(templateSize?.[0])
            : undefined;
          const height = !Number.isNaN(Number(templateSize?.[1]))
            ? Number(templateSize?.[1])
            : undefined;

          if (width === undefined || height === undefined) {
            return template;
          }

          return {
            ...template,
            meta: {
              width,
              height,
            },
          };
        }),
    [polotnoTemplatesData?.pages],
  );

  const unsplashEntries = useMemo(
    () =>
      (searchString && unsplashResp?.pages.flatMap(({ results }) => results)) ||
      [],
    [searchString, unsplashResp?.pages],
  );

  const fetchNextPolotnoTemplates = useCallback(() => {
    if (isTemplatesFetching || !hasPolotnoTemplatesNextPage) {
      return;
    }

    polotnoTemplatesFetchNextPage();
  }, [
    hasPolotnoTemplatesNextPage,
    isTemplatesFetching,
    polotnoTemplatesFetchNextPage,
  ]);

  useIsomorphicLayoutEffect(() => {
    onChangeRelevantFlag && onChangeRelevantFlag(!!polotnoTemplates?.length);
  }, [onChangeRelevantFlag, polotnoTemplates?.length]);

  useUpdateEffect(() => {
    if (searchString) {
      yaReachGoal("polotno_templates_search", { query: searchString });
    }
  }, [searchString]);

  if (isTemplatesLoading || isUnsplashLoading) {
    return (
      <UiBox display="flex" justifyContent="center" height="100%">
        <UiSpinner title={<Trans>Loading</Trans>} />
      </UiBox>
    );
  }

  if (!unsplashEntries.length && !polotnoTemplates?.length) {
    return <NothingFound />;
  }

  return (
    <VirtualGrid
      templates={polotnoTemplates || []}
      unsplashEntries={unsplashEntries}
      inViewTemplateLoader={
        hasPolotnoTemplatesNextPage ? fetchNextPolotnoTemplates : undefined
      }
      hasTemplatesNextPage={!!hasPolotnoTemplatesNextPage}
    />
  );
};
